// Device-detail lifecycle action bar: disable / enable / report-lost. Each sensitive
// action goes through ConfirmDialog stating the consequence; report-lost carries a
// prominent irreversible-intent warning. All are server-side audited (LOCKED rules).
//
// The confirm dialog collects an OPTIONAL reason + case_id (the backend accepts
// {reason, case_id} on every device action — admin.go adminActionRequest) so the
// audit trail can carry context. report-lost is treated as the most serious (danger
// tone + warning banner).

import { useState } from 'react';
import { canAdminWrite, useAuth } from '../../auth/AuthContext';
import { actionErrorMessage } from '../_shared/actionError';
import { ConfirmDialog } from '../_shared/ConfirmDialog';
import type { ConfirmTone } from '../_shared/ConfirmDialog';
import { deviceActionGate, deviceName } from './deviceModel';
import { deviceAction } from './devicesApi';
import type { AdminDevice, DeviceAction } from './devicesApi';
import './devices.css';

interface ActionSpec {
  key: DeviceAction;
  label: string;
  enabled: boolean;
  btnClass: string;
  tone: ConfirmTone;
  title: string;
  consequence: string;
  confirmLabel: string;
  warning?: string;
  /** whether report-style reason capture is shown (all device actions accept it) */
  requireReasonHint?: string;
}

interface Result {
  action: DeviceAction;
  ok: boolean;
  message: string;
  requestId?: string | null;
}

export function DeviceActionBar({ device, onChanged }: { device: AdminDevice; onChanged: () => void }) {
	const { role } = useAuth();
	const gate = deviceActionGate(device);
	const name = deviceName(device);

  const [pending, setPending] = useState<ActionSpec | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
	const [reason, setReason] = useState('');
	const [caseId, setCaseId] = useState('');
	const [dialogError, setDialogError] = useState<string | null>(null);

	if (!canAdminWrite(role)) {
		return (
			<div className="card adm-actionbar card--primary accent-rail--neutral">
				<div className="adm-actionbar-head">
					<h2 className="adm-card-title">裝置處置</h2>
					<span className="pill pill-neutral">唯讀</span>
				</div>
				<p className="text-secondary" style={{ margin: 0 }}>
					目前角色僅能檢視裝置資料與稽核紀錄。
				</p>
			</div>
		);
	}

	const specs: ActionSpec[] = [
    {
      key: 'disable',
      label: '停用裝置',
      enabled: gate.disable,
      btnClass: 'btn-ghost',
      tone: 'warn',
      title: `停用 ${name}？`,
      consequence:
        '停用後此裝置將無法連線、串流或上傳錄影，直到重新啟用為止。擁有者 App 內會看到裝置離線。此操作會被稽核記錄。',
      confirmLabel: '確認停用',
      requireReasonHint: '建議填寫停用原因與案件編號以利稽核。',
    },
    {
      key: 'enable',
      label: '重新啟用',
      enabled: gate.enable,
      btnClass: 'btn-primary',
      tone: 'primary',
      title: `重新啟用 ${name}？`,
      consequence: '重新啟用後此裝置將恢復連線與錄影功能。此操作會被稽核記錄。',
      confirmLabel: '確認啟用',
    },
    {
      key: 'report-lost',
      label: '標記掛失',
      enabled: gate.reportLost,
      btnClass: 'btn-danger',
      tone: 'danger',
      title: `將 ${name} 標記為掛失？`,
      consequence:
        '標記掛失會停用此裝置並標示為遺失/遭竊狀態，通常用於協助使用者處理失竊。這是嚴重處置，請確認你有足夠依據再執行。此操作會被稽核記錄。',
      confirmLabel: '我了解，標記掛失',
      warning: '高風險操作：掛失會立即停用裝置並改變其資產狀態，且全程稽核留痕。',
      requireReasonHint: '請填寫掛失原因（如使用者通報失竊）與案件編號。',
    },
  ];

  function openConfirm(spec: ActionSpec) {
    setResult(null);
    setReason('');
    setCaseId('');
    setDialogError(null);
    setPending(spec);
  }

  function closeConfirm() {
    if (!busy) {
      setPending(null);
      setDialogError(null);
    }
  }

  async function run(spec: ActionSpec) {
    setBusy(true);
    setDialogError(null);
    try {
      const res = await deviceAction(device.device_id, spec.key, { reason: reason.trim() || undefined, caseId: caseId.trim() || undefined });
      setResult({
        action: spec.key,
        ok: true,
        message: `${spec.label}成功${res.status ? `（狀態：${res.status}）` : ''}。`,
        requestId: res.requestId,
      });
      setPending(null);
      onChanged();
    } catch (e) {
      // Keep the dialog open and show the error inline so the admin can retry.
      // actionErrorMessage maps the distinct 403 codes (e.g. ADMIN_REGION_MISMATCH).
      setDialogError(actionErrorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  // 依「掛失是否仍可執行」決定左側 accent 是否上警戒色:掛失可執行時視覺上更醒目提醒這是高風險區
  const railTone = gate.reportLost ? 'warn' : 'neutral';

  return (
    <div className={`card adm-actionbar card--primary accent-rail--${railTone}`}>
      <div className="adm-actionbar-head">
        <h2 className="adm-card-title">裝置處置</h2>
        {/* 合規提示改用 pill(比照 UserActionBar,與「唯讀」pill 同視覺重量,不再是易忽略的小字) */}
        <span className="pill pill-info">所有處置均會稽核留痕</span>
      </div>

      <div className="adm-actionbar-btns">
        {specs.map((s) => (
          <button
            key={s.key}
            className={s.btnClass}
            disabled={!s.enabled || busy}
            title={s.enabled ? s.consequence : '目前裝置狀態不適用此操作'}
            onClick={() => openConfirm(s)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {result && (
        <div className={`adm-action-result ${result.ok ? 'is-ok' : 'is-err'}`} role="status">
          <p style={{ margin: 0 }}>{result.message}</p>
          {result.requestId && (
            <p className="adm-mono adm-result-meta" style={{ margin: 0, opacity: 0.85 }}>
              request_id: {result.requestId}
            </p>
          )}
        </div>
      )}

      {pending && (
        <ConfirmDialog
          title={pending.title}
          tone={pending.tone}
          warning={pending.warning}
          confirmLabel={pending.confirmLabel}
          busy={busy}
          error={dialogError}
          onConfirm={() => void run(pending)}
          onCancel={closeConfirm}
          body={
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
              <p style={{ margin: 0 }}>{pending.consequence}</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">原因（選填）</span>
                  <input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    disabled={busy}
                    placeholder={pending.requireReasonHint ? '例：使用者通報失竊' : '原因'}
                    style={{ width: '100%' }}
                  />
                </label>
                <label className="adm-field">
                  <span className="text-secondary adm-field-label">案件編號（選填）</span>
                  <input
                    value={caseId}
                    onChange={(e) => setCaseId(e.target.value)}
                    disabled={busy}
                    placeholder="例：CS-1234"
                    style={{ width: '100%' }}
                  />
                </label>
                {/* 提示語比照 UserActionBar:欄位下方 adm-hint 段落,而非 label 內嵌 ⓘ tooltip */}
                {pending.requireReasonHint && <p className="adm-hint" style={{ marginTop: 0 }}>{pending.requireReasonHint}</p>}
              </div>
            </div>
          }
        />
      )}
    </div>
  );
}
